import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Pencil, Trash2, Check, X, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Plan Management — Settings → Module Settings → Hotel Onboarded/Accounts
 * --------------------------------------------------------------------------
 * Lets admins maintain the master list of plans that powers the "Interested
 * Plan" dropdown inside the Add Account → Commercials section.
 *
 * Backend surface: /api/account-plans  (GET / POST / PUT / DELETE)
 *
 * Inline row editor avoids any modal/popup churn for this lightweight CRUD.
 * No existing settings or shared constants are mutated — this section is
 * additive only.
 */
const formatPrice = (n) => {
  if (n === null || n === undefined || n === '') return '';
  const v = Number(n);
  if (Number.isNaN(v)) return n;
  return v.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

const PlanManagement = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null); // id of row currently saving/deleting
  const [adding, setAdding] = useState(false);
  const [newPlan, setNewPlan] = useState({ name: '', price: '' });
  const [editing, setEditing] = useState(null); // { id, name, price }

  const authHeader = useCallback(() => {
    const t = localStorage.getItem('token') || localStorage.getItem('access_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const fetchPlans = useCallback(async () => {
    setError('');
    try {
      const res = await fetch(`${API}/api/account-plans`, { headers: authHeader() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPlans(data.data || []);
    } catch (e) {
      setError('Could not load plans');
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const handleCreate = async () => {
    const name = (newPlan.name || '').trim();
    const price = parseFloat(newPlan.price);
    if (!name) { setError('Plan name is required'); return; }
    if (Number.isNaN(price) || price < 0) { setError('Plan price must be a non-negative number'); return; }
    setBusy('__new__'); setError('');
    try {
      const res = await fetch(`${API}/api/account-plans`, {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, price }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || `HTTP ${res.status}`);
      setNewPlan({ name: '', price: '' });
      setAdding(false);
      await fetchPlans();
    } catch (e) {
      setError(e.message || 'Failed to create plan');
    } finally {
      setBusy(null);
    }
  };

  const handleUpdate = async (id) => {
    const name = (editing.name || '').trim();
    const price = parseFloat(editing.price);
    if (!name) { setError('Plan name is required'); return; }
    if (Number.isNaN(price) || price < 0) { setError('Plan price must be a non-negative number'); return; }
    setBusy(id); setError('');
    try {
      const res = await fetch(`${API}/api/account-plans/${id}`, {
        method: 'PUT',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, price }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || `HTTP ${res.status}`);
      setEditing(null);
      await fetchPlans();
    } catch (e) {
      setError(e.message || 'Failed to update plan');
    } finally {
      setBusy(null);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete plan "${name}"? This will not affect existing Accounts already using it, but it will no longer appear in the Interested Plan dropdown for new entries.`)) return;
    setBusy(id); setError('');
    try {
      const res = await fetch(`${API}/api/account-plans/${id}`, {
        method: 'DELETE',
        headers: authHeader(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      await fetchPlans();
    } catch (e) {
      setError(e.message || 'Failed to delete plan');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm" data-testid="plan-management">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Plan Management</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Plans listed here populate the <strong>Interested Plan</strong> dropdown in
            Add Account → Commercials. The selected plan's price auto-fills the
            "One Time Setup Fee" field.
          </p>
        </div>
        {!adding && (
          <button
            onClick={() => { setAdding(true); setError(''); setEditing(null); }}
            className="flex items-center gap-1.5 px-3 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm font-medium"
            data-testid="plan-mgmt-add-btn"
          >
            <Plus size={16} /> Add Plan
          </button>
        )}
      </div>

      {error && (
        <div className="mb-3 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="plan-mgmt-error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500 py-8 justify-center">
          <Loader2 size={18} className="animate-spin" /> Loading plans…
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium">Plan Name</th>
                <th className="px-4 py-2.5 text-left font-medium">Plan Price (₹)</th>
                <th className="px-4 py-2.5 text-right font-medium w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {adding && (
                <tr className="border-t border-gray-100 bg-teal-50/40" data-testid="plan-mgmt-add-row">
                  <td className="px-4 py-2">
                    <input
                      autoFocus
                      type="text"
                      placeholder="e.g. Basic, Premium"
                      value={newPlan.name}
                      onChange={(e) => setNewPlan(p => ({ ...p, name: e.target.value }))}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 outline-none"
                      data-testid="plan-mgmt-new-name"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={newPlan.price}
                      onChange={(e) => setNewPlan(p => ({ ...p, price: e.target.value }))}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 outline-none"
                      data-testid="plan-mgmt-new-price"
                    />
                  </td>
                  <td className="px-4 py-2 text-right space-x-1">
                    <button
                      onClick={handleCreate}
                      disabled={busy === '__new__'}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
                      data-testid="plan-mgmt-save-new"
                      title="Save"
                    >
                      {busy === '__new__' ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                    </button>
                    <button
                      onClick={() => { setAdding(false); setNewPlan({ name: '', price: '' }); setError(''); }}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50"
                      title="Cancel"
                      data-testid="plan-mgmt-cancel-new"
                    >
                      <X size={14} />
                    </button>
                  </td>
                </tr>
              )}
              {plans.length === 0 && !adding && (
                <tr><td colSpan={3} className="px-4 py-8 text-center text-gray-400 italic">No plans configured yet. Click "Add Plan" to create your first one.</td></tr>
              )}
              {plans.map((p) => {
                const isEditing = editing?.id === p.id;
                return (
                  <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50/60" data-testid={`plan-row-${p.id}`}>
                    <td className="px-4 py-2">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editing.name}
                          onChange={(e) => setEditing(s => ({ ...s, name: e.target.value }))}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 outline-none"
                          data-testid={`plan-edit-name-${p.id}`}
                        />
                      ) : (
                        <span className="font-medium text-gray-800">{p.name}</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {isEditing ? (
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={editing.price}
                          onChange={(e) => setEditing(s => ({ ...s, price: e.target.value }))}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 outline-none"
                          data-testid={`plan-edit-price-${p.id}`}
                        />
                      ) : (
                        <span className="text-gray-700">₹ {formatPrice(p.price)}</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right space-x-1">
                      {isEditing ? (
                        <>
                          <button
                            onClick={() => handleUpdate(p.id)}
                            disabled={busy === p.id}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
                            title="Save"
                            data-testid={`plan-save-${p.id}`}
                          >
                            {busy === p.id ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                          </button>
                          <button
                            onClick={() => { setEditing(null); setError(''); }}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50"
                            title="Cancel"
                            data-testid={`plan-cancel-${p.id}`}
                          >
                            <X size={14} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => { setEditing({ id: p.id, name: p.name, price: p.price }); setError(''); setAdding(false); }}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50"
                            title="Edit"
                            data-testid={`plan-edit-btn-${p.id}`}
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            onClick={() => handleDelete(p.id, p.name)}
                            disabled={busy === p.id}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
                            title="Delete"
                            data-testid={`plan-delete-btn-${p.id}`}
                          >
                            {busy === p.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PlanManagement;
